import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/commerce-webapi/commerce-webapi/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "mergecarts-mutation"
    }}>{`mergeCarts mutation`}</h1>
    <p>{`The `}<inlineCode parentName="p">{`mergeCarts`}</inlineCode>{` mutation transfers the contents of a guest cart into the cart of a logged-in customer. This mutation must be run on behalf of a logged-in customer.`}</p>
    <p>{`If you do not specify a value for the `}<inlineCode parentName="p">{`destination_cart_id`}</inlineCode>{` input argument, the mutation determines the customer's cart ID and uses that value.`}</p>
    <p>{`The mutation retains any items that were already in the logged-in customer's cart. If both the guest and customer carts contain the same item, `}<inlineCode parentName="p">{`mergeCarts`}</inlineCode>{` adds the quantities. Upon success, the mutation deletes the original guest cart.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`Use the `}<a parentName="p" {...{
        "href": "assign-customer-to-guest-cart.md"
      }}>{`assignCustomerToGuestCart`}</a>{` mutation to assign the contents of a logged-in customer's cart to a guest cart.
Customer cart becomes inactive and the guest cart remains active.`}</p>
    <h2 {...{
      "id": "syntax"
    }}>{`Syntax`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-graphql"
      }}>{`mutation {
    mergeCarts(
        source_cart_id: String!
        destination_cart_id: String
    ) {
        Cart!
    }
}
`}</code></pre>
    <h2 {...{
      "id": "reference"
    }}>{`Reference`}</h2>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.adobe.com/commerce/webapi/graphql-api/index.html#mutation-mergeCarts"
      }}><inlineCode parentName="a">{`mergeCarts`}</inlineCode></a>{` reference provides detailed information about the types and fields defined in this mutation.`}</p>
    <h2 {...{
      "id": "example-usage"
    }}>{`Example usage`}</h2>
    <p>{`In the following example, the customer had one Overnight Duffle in the cart (`}<inlineCode parentName="p">{`CYmiiQRjPVc2gJUc5r7IsBmwegVIFO43`}</inlineCode>{`) before a guest cart (`}<inlineCode parentName="p">{`mPKE05OOtcxErbk1Toej6gw6tcuxvT9O`}</inlineCode>{`) containing a Radiant Tee and another Overnight Duffle was merged. The cart now includes three items, including two Overnight Duffles.`}</p>
    <p><strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-graphql"
      }}>{`mutation {
  mergeCarts(
    source_cart_id: "mPKE05OOtcxErbk1Toej6gw6tcuxvT9O",
    destination_cart_id: "CYmiiQRjPVc2gJUc5r7IsBmwegVIFO43"
  ) {
    itemsV2 {
      items {
        id
        product {
          name
          sku
        }
        quantity
      }
      total_count
      page_info {
        page_size
        current_page
        total_pages
      }
    }
  }
}
`}</code></pre>
    <p><strong parentName="p">{`Response:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "data": {
    "mergeCarts": {
      "itemsV2": {
        "items": [
          {
            "id": "14",
            "product": {
              "name": "Overnight Duffle",
              "sku": "24-WB07"
            },
            "quantity": 2
          },
          {
            "id": "17",
            "product": {
              "name": "Radiant Tee",
              "sku": "WS12"
            },
            "quantity": 1
          }
        ],
        "total_count": 2,
        "page_info": {
          "page_size": 20,
          "current_page": 1,
          "total_pages": 1
        }
      }
    }
  }
}
`}</code></pre>
    <p>{`The following example executes the previous request without specifying `}<inlineCode parentName="p">{`destination_cart_id`}</inlineCode>{`.`}</p>
    <p><strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-graphql"
      }}>{`mutation {
  mergeCarts(
    source_cart_id: "mPKE05OOtcxErbk1Toej6gw6tcuxvT9O"
  ) {
    itemsV2 {
      items {
        id
        product {
          name
          sku
        }
        quantity
      }
      total_count
      page_info {
        page_size
        current_page
        total_pages
      }
    }
  }
}
`}</code></pre>
    <p><strong parentName="p">{`Response:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "data": {
    "mergeCarts": {
      "itemsV2": {
        "items": [
          {
            "id": "14",
            "product": {
              "name": "Overnight Duffle",
              "sku": "24-WB07"
            },
            "quantity": 2
          },
          {
            "id": "17",
            "product": {
              "name": "Radiant Tee",
              "sku": "WS12"
            },
            "quantity": 1
          }
        ]
      },
      "total_count": 2,
      "page_info": {
        "page_size": 20,
        "current_page": 1,
        "total_pages": 1
      }
    }
  }
}
`}</code></pre>
    <h2 {...{
      "id": "errors"
    }}>{`Errors`}</h2>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Error`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`Current user does not have an active cart.`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The `}<inlineCode parentName="td">{`mergeCarts`}</inlineCode>{` mutation deactivates the guest cart specified in the `}<inlineCode parentName="td">{`source_cart_id`}</inlineCode>{` after merging. The guest cannot make any further operations with it.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`Required parameter "source_cart_id" is missing`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The `}<inlineCode parentName="td">{`source_cart_id`}</inlineCode>{` attribute contains an empty value.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`The current customer isn't authorized.`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The current customer is not currently logged in, or the customer's token does not exist in the `}<inlineCode parentName="td">{`oauth_token`}</inlineCode>{` table, or you tried to merge two guest carts.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`The current user cannot perform operations on cart`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The authorized customer tried to merge a guest cart into the cart of another customer.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`Could not create empty cart for customer`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The system could not create an empty cart for the logged-in customer`}</td>
        </tr>
      </tbody>
    </table>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      